/* hx-quiz.js — runtime for sections/hx-quiz.liquid.
   Plain, readable, no build step. Reads the section's JSON config and runs
   the funnel: questions → analysing ring → stepped loader → scored result →
   email / SMS capture → recommendations. Reports the same events as the
   legacy quiz (GA4 via gtag with send_to, qlp_* dataLayer events, Klaviyo
   track / identify / subscribe, cart attributes, optional beacon) so
   reporting carries on unbroken. See docs/hx-quiz.md. */
(function () {
  'use strict';

  var TAG = '[hx-quiz]';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Final-word accent, the JS twin of snippets/hx-fw.liquid. Escapes its input.
  function fw(text) {
    var words = String(text || '').trim().split(/\s+/).filter(Boolean);
    if (!words.length) return '';
    var last = words.pop();
    return (words.length ? esc(words.join(' ')) + ' ' : '') + '<em class="fw">' + esc(last) + '</em>';
  }

  function joinAnd(list) {
    if (list.length < 2) return list[0] || '';
    return list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1];
  }

  function uniq(list) {
    var seen = {};
    return list.filter(function (x) { if (!x || seen[x]) return false; seen[x] = 1; return true; });
  }

  function moneyFormatter(format) {
    var fmt = format || '${{amount}}';
    function thousands(s, sep) { return s.replace(/\B(?=(\d{3})+(?!\d))/g, sep); }
    return function (cents) {
      var n = (Number(cents) || 0) / 100;
      var parts = n.toFixed(2).split('.');
      var whole = parts[0], dec = parts[1], round = String(Math.round(n));
      return fmt
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, thousands(round, '.'))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, thousands(whole, '.') + ',' + dec)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, thousands(round, ','))
        .replace(/\{\{\s*amount\s*\}\}/g, thousands(whole, ',') + '.' + dec)
        .replace(/<[^>]+>/g, '');
    };
  }

  // Phone → E.164 for Klaviyo (Australian default), as the legacy quiz did.
  function e164(phone) {
    if (!phone) return null;
    var t = String(phone).replace(/[^\d+]/g, '');
    if (t.indexOf('+') === 0) return t;
    if (t.indexOf('0') === 0) return '+61' + t.slice(1);
    if (t.indexOf('61') === 0) return '+' + t;
    return '+61' + t;
  }

  function fail(root, message) {
    var screen = root.querySelector('[data-quiz-screen]');
    if (screen) screen.innerHTML = '<section class="qq"><h1>This quiz is not set up yet</h1><p>' + esc(message) + '</p></section>';
    console.warn(TAG, message);
  }

  function init(root) {
    if (root.__hxQuiz) return;
    root.__hxQuiz = true;

    var cfg;
    try {
      cfg = JSON.parse(root.querySelector('[data-quiz-config]').textContent);
    } catch (e) {
      fail(root, 'the quiz settings could not be read (' + e.message + '). Usually a curly quote in a setting: retype it with a straight quote.');
      return;
    }

    var ds = root.dataset;
    var quizId = ds.quizId || 'quiz';
    var flow = cfg.flow || {};
    var inter = cfg.interstitial || {};
    var scored = cfg.scored || {};
    var finder = cfg.finder || {};
    var cap = cfg.capture || {};
    var catalog = cfg.catalog || {};
    var collections = cfg.collections || {};
    var offer = cfg.offer || null;   // sections/hx-quiz-offer: the top match becomes a buy box
    var reveal = cfg.reveal || {};
    var revealOn = !!reveal.enabled && !offer;   // the product page under the result, see showReveal
    var money = moneyFormatter(ds.moneyFormat);
    var questions = (cfg.questions || []).filter(function (q) {
      return q && q.text && (q.type === 'input' || (q.options || []).length);
    });
    var total = questions.length;

    var el = {
      stage: root.querySelector('[data-quiz-stage]'),
      screen: root.querySelector('[data-quiz-screen]'),
      back: root.querySelector('[data-quiz-back]'),
      step: root.querySelector('[data-quiz-step]'),
      bar: root.querySelector('[data-quiz-bar]'),
      exit: root.querySelector('[data-quiz-exit]'),
      results: root.querySelector('[data-quiz-results]'),
      eyebrow: root.querySelector('[data-res-eyebrow]'),
      title: root.querySelector('[data-res-title]'),
      sub: root.querySelector('[data-res-sub]'),
      why: root.querySelector('[data-res-why]'),
      picks: root.querySelector('[data-res-picks]'),
      actions: root.querySelector('[data-res-actions]'),
      single: root.querySelector('[data-res-single]'),
      capture: root.querySelector('[data-res-capture]'),
      alts: root.querySelector('[data-res-alts]'),
      retake: root.querySelector('[data-quiz-retake]')
    };

    if (!total) {
      var n = (cfg.questions || []).length;
      fail(root, n ? 'each question needs at least one answer label (or the text-input switch on).' : 'add a Question block with a question and at least one answer.');
      return;
    }

    // ------------------------------------------------------------ state
    var storeKey = 'hx_quiz:' + quizId;

    function fresh() {
      return {
        sid: 's_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10),
        step: 0, answers: {}, labels: {}, interShown: false, startDone: false,
        gap: '', verdict: '', score: null, level: '',
        email: '', phone: '', discount: '', smsSkipped: false, doubled: false, done: false, started: false, revealAt: 0
      };
    }
    function load() {
      try {
        var raw = sessionStorage.getItem(storeKey);
        var s = raw ? JSON.parse(raw) : null;
        return s && s.answers && typeof s.step === 'number' ? s : null;
      } catch (e) { return null; }
    }
    function save() { try { sessionStorage.setItem(storeKey, JSON.stringify(state)); } catch (e) { /* private mode */ } }
    function clear() { try { sessionStorage.removeItem(storeKey); } catch (e) { /* no-op */ } }

    var state = load() || fresh();

    // ------------------------------------------------------------ tracking
    // Same contract as the legacy quiz: Klaviyo track (display name), the
    // qlp_* dataLayer event (GTM triggers depend on it), a DOM event, GA4 via
    // gtag (snake_case name, flat params, send_to the quiz's GA4 id) and the
    // optional backend beacon.
    function track(name, props) {
      props = Object.assign({ quiz_id: quizId, session_id: state.sid }, props || {});
      var key = 'qlp_' + name.replace(/\s+/g, '_').toLowerCase();
      if (window.klaviyo && typeof window.klaviyo.push === 'function') window.klaviyo.push(['track', name, props]);
      if (window.dataLayer) window.dataLayer.push(Object.assign({ event: key }, props));
      root.dispatchEvent(new CustomEvent('qlp:event', { bubbles: true, detail: { name: name, props: props } }));
      if (typeof window.gtag === 'function') {
        var flat = {};
        Object.keys(props).forEach(function (k) {
          var v = props[k];
          if (v !== null && typeof v !== 'object') flat[k] = v;
        });
        if (ds.ga4Id) flat.send_to = ds.ga4Id;
        window.gtag('event', name.replace(/\s+/g, '_').toLowerCase(), flat);
      }
      if (ds.endpoint) {
        try {
          var body = JSON.stringify({ event: key, quiz_id: quizId, session_id: state.sid, ts: new Date().toISOString(), props: props });
          if (navigator.sendBeacon) navigator.sendBeacon(ds.endpoint, new Blob([body], { type: 'application/json' }));
          else fetch(ds.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true }).catch(function () {});
        } catch (e) { /* never block the quiz on reporting */ }
      }
    }

    // ------------------------------------------------------------ answers → scores
    function isNameQ(q) { return q.type === 'input' && (q.id === 'first_name' || q.id === 'name' || /name/i.test(q.id)); }
    function findOption(q, value) {
      for (var i = 0; i < (q.options || []).length; i++) if (q.options[i].value === value) return q.options[i];
      return null;
    }

    // Everything the results need, recomputed from the answers each time so
    // going back and changing an answer never double-counts.
    function derive() {
      var d = { products: {}, collections: {}, reasons: {}, match: '', name: '', score: null };
      questions.forEach(function (q) {
        var v = state.answers[q.id];
        if (v == null || v === '') return;
        if (q.type === 'input') { if (isNameQ(q)) d.name = v; return; }
        var opt = findOption(q, v);
        if (!opt) return;
        var w = +opt.weight || 1;
        (opt.products || []).forEach(function (h) {
          if (!h) return;
          d.products[h] = (d.products[h] || 0) + w;
          if (!q.hideInRecap) (d.reasons[h] = d.reasons[h] || []).push(opt.label);
        });
        (opt.collections || []).forEach(function (h) { if (h) d.collections[h] = (d.collections[h] || 0) + w; });
        if (q.matchDriver && opt.match) d.match = opt.match;
      });
      d.score = scoreAnswers();
      return d;
    }

    // Scored result: chosen score ÷ the best possible score per answered
    // question. Higher = more in the way. Each bar is the points its answers
    // earned ÷ the most the answered questions could have given that bar, so
    // a bar reads how much of that blocker's evidence was ticked: four
    // answers pointing at cravings outrank one strong answer pointing
    // elsewhere. The highest bar is the gap.
    function scoreAnswers() {
      var cats = { 1: { sum: 0, max: 0 }, 2: { sum: 0, max: 0 }, 3: { sum: 0, max: 0 } }, sum = 0, max = 0;
      questions.forEach(function (q) {
        if (q.type !== 'options') return;
        var v = state.answers[q.id];
        if (v == null) return;
        var chosen = null, top = 0, tops = { 1: 0, 2: 0, 3: 0 };
        q.options.forEach(function (o) {
          var sc = +o.score || 0, c = +o.cat || 0;
          if (sc > top) top = sc;
          if (tops[c] != null && sc > tops[c]) tops[c] = sc;
          if (o.value === v) chosen = o;
        });
        if (!chosen || top <= 0) return;
        var sc = +chosen.score || 0;
        sum += sc; max += top;
        for (var k in cats) cats[k].max += tops[k];
        var c = +chosen.cat || 0;
        if (cats[c]) cats[c].sum += sc;
      });
      function pct(o) { return o.max > 0 ? Math.round(o.sum / o.max * 100) : 0; }
      return { overall: max > 0 ? Math.round(sum / max * 100) : 50, bars: [pct(cats[1]), pct(cats[2]), pct(cats[3])] };
    }

    function rank(map, n) {
      return Object.keys(map).sort(function (a, b) { return map[b] - map[a]; }).slice(0, n);
    }

    function pctNow() {
      var code = state.discount;
      if (!code) return 0;
      if (ds.discountCodeSms && code === ds.discountCodeSms) return +ds.discountPctSms || 0;
      if (ds.discountCode && code === ds.discountCode) return +ds.discountPct || 0;
      return 0;
    }

    // {name} / {gap} / {verdict} placeholders from the legacy copy, plus the
    // theme's [product] / [collection] / [n] ones.
    function fill(text, extra) {
      if (text == null) return '';
      var d = derive();
      var map = {
        name: d.name || '', gap: state.gap || 'your biggest gap', verdict: state.verdict || 'your result',
        product: extra && extra.product || '', collection: extra && extra.collection || '', n: extra && extra.n != null ? String(extra.n) : '',
        score: state.score != null ? String(state.score) : '',
        count: extra && extra.count != null ? String(extra.count) : '', answers: String(Object.keys(state.answers).length)
      };
      return String(text)
        .replace(/\{name\},?\s*/gi, d.name ? d.name + ', ' : '')
        .replace(/[{\[](name|gap|verdict|product|collection|n|score|count|answers)[}\]]/gi, function (_, k) { return map[k.toLowerCase()] || ''; });
    }

    // ------------------------------------------------------------ chrome
    function paint() {
      var i = Math.min(state.step, total);
      var pct = total ? Math.round((Math.min(i + 1, total) / total) * 100) : 0;
      if (el.bar) el.bar.style.width = (state.done ? 100 : pct) + '%';
      if (el.step) el.step.textContent = flow.showProgress !== false && i < total && !state.done
        ? ((flow.stepWord ? flow.stepWord + ' ' : '') + (i + 1) + ' of ' + total) : '';
      if (el.back) el.back.classList.toggle('off', !(state.step > 0 && !state.done));
    }

    function toTop() {
      var top = root.getBoundingClientRect().top;
      if (top < 0) window.scrollTo({ top: window.scrollY + top - 16, behavior: 'smooth' });
    }

    function showStage(html) {
      el.results.hidden = true;
      el.stage.hidden = false;
      el.screen.innerHTML = html;
      paint();
      toTop();
    }

    // ------------------------------------------------------------ questions
    // ------------------------------------------------------------ customer review under the answers
    // A review block is a before and after entry, or a photo, quote and name typed in the
    // customiser. The blocks rotate with the step: the first on the first question, the next on
    // the next, round again, so a short quiz shows each one and a long one repeats them.
    function reviewHtml(idx) {
      var list = flow.reviews || [];
      if (!list.length) return '';
      var r = list[((idx % list.length) + list.length) % list.length];
      if (!r || (!r.quote && !r.image)) return '';
      return '<figure class="qrev">' + (r.image ? '<img src="' + esc(r.image) + '" alt="" width="120" height="120" loading="lazy">' : '') +
        '<figcaption>' + (r.quote ? '<p>' + esc(r.quote) + '</p>' : '') +
        (r.name || r.tag ? '<span>' + (r.name ? '<b>' + esc(r.name) + '</b>' : '') + (r.name && r.tag ? ' \u00b7 ' : '') + esc(r.tag || '') + '</span>' : '') +
        '</figcaption></figure>' + (flow.reviewNote ? '<p class="qrev-note">' + esc(flow.reviewNote) + '</p>' : '');
    }

    function showQuestion() {
      el.stage.classList.remove('is-start');
      var q = questions[state.step];
      var html = '';
      if (state.step === 0 && flow.intro) html += '<div class="qintro">' + flow.intro + '</div>';
      if (q.image) html += '<div class="qimg"><img src="' + esc(q.image) + '" alt="" loading="lazy"></div>';
      html += '<section class="qq"><h1>' + esc(q.text) + '</h1>' + (q.help || '') + '</section>';
      if (q.type === 'input') {
        html += '<div class="qform"><input type="text" class="qinput" data-quiz-input placeholder="' + esc(q.placeholder || '') + '" value="' + esc(state.labels[q.id] || '') + '" autocomplete="' + (isNameQ(q) ? 'given-name' : 'off') + '" maxlength="60"></div>';
        html += '<div class="qnav"><button type="button" class="btn" data-quiz-next>' + esc(q.cta || 'Continue') + '</button></div>';
      } else {
        html += '<div class="opts" role="group" aria-label="' + esc(q.text) + '">' + q.options.map(function (o, i) {
          var on = state.answers[q.id] === o.value;
          return '<button type="button" class="opt' + (on ? ' on' : '') + '" data-i="' + i + '" aria-pressed="' + on + '">' +
            (o.icon ? '<img class="ic" src="' + esc(o.icon) + '" alt="" width="24" height="24" loading="lazy">' : '') +
            '<div><b>' + esc(o.label) + '</b>' + (o.sub ? '<span>' + esc(o.sub) + '</span>' : '') + '</div></button>';
        }).join('') + '</div>';
        html += '<div class="qreact" data-quiz-react hidden></div>';
        html += '<div class="qnav"><button type="button" class="btn" data-quiz-next' + (state.answers[q.id] == null ? ' disabled' : '') + '>' + esc(flow.nextLabel || 'Next') + '</button></div>';
      }
      if (flow.reviewScreens === 'questions' || flow.reviewScreens === 'both') html += reviewHtml(state.step);
      showStage(html);
      wireQuestion(q);
    }

    function wireQuestion(q) {
      var next = el.screen.querySelector('[data-quiz-next]');
      if (q.type === 'input') {
        var input = el.screen.querySelector('[data-quiz-input]');
        var go = function () {
          var v = (input.value || '').trim().replace(/[<>]/g, '').slice(0, 60);
          state.answers[q.id] = v;
          state.labels[q.id] = v;
          save();
          track('Quiz Question Answered', { q: q.id, answer: v ? 'filled' : 'skipped', step: state.step + 1 });
          advance();
        };
        next.addEventListener('click', go);
        input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); go(); } });
        setTimeout(function () { try { input.focus({ preventScroll: true }); } catch (e) { /* no-op */ } }, 50);
        return;
      }
      var opts = el.screen.querySelectorAll('.opt');
      var react = el.screen.querySelector('[data-quiz-react]');
      var timer = null;
      Array.prototype.forEach.call(opts, function (btn) {
        btn.addEventListener('click', function () {
          var o = q.options[+btn.dataset.i];
          if (!o) return;
          Array.prototype.forEach.call(opts, function (b) { b.classList.remove('on'); b.setAttribute('aria-pressed', 'false'); });
          btn.classList.add('on');
          btn.setAttribute('aria-pressed', 'true');
          state.answers[q.id] = o.value;
          state.labels[q.id] = o.label;
          save();
          next.disabled = false;
          // an offer quiz sits mid-page, so it starts on the first answer rather than on page load
          if (offer && !state.started) { state.started = true; save(); track('Quiz Started', { total_questions: total }); }
          track('Quiz Question Answered', { q: q.id, answer: o.value, step: state.step + 1 });
          if (q.reaction) { react.textContent = q.reaction; react.hidden = false; }
          if (timer) clearTimeout(timer);
          if (flow.autoAdvance !== false) {
            Array.prototype.forEach.call(opts, function (b) { b.disabled = true; });
            timer = setTimeout(advance, q.reaction ? 1400 : 350);
          }
        });
      });
      next.addEventListener('click', function () {
        if (timer) clearTimeout(timer);
        if (state.answers[q.id] != null) advance();
      });
    }

    function advance() {
      var answered = state.step + 1;
      state.step = answered;
      save();
      if (answered < total) {
        if (inter.after && answered === inter.after && !state.interShown) return showInterstitial(showQuestion);
        return showQuestion();
      }
      endQuestions();
    }

    function goBack() {
      if (state.done || state.step <= 0) return;
      state.step = Math.min(state.step, total) - 1;
      save();
      showQuestion();
    }

    function endQuestions() {
      var afterScored = function () {
        if (cap.mode === 'gate' && !state.email && ds.discountCode) showGate(finish);
        else finish(false);
      };
      var maybeScored = function () { if (scored.enabled) showScored(afterScored); else afterScored(); };
      if (inter.finalLoader) showFinalLoader(maybeScored); else maybeScored();
    }

    // ------------------------------------------------------------ loaders
    var RING = 2 * Math.PI * 52; // circumference of the r=52 ring in the 120-unit viewBox

    function ring(cls) {
      return '<svg viewBox="0 0 120 120" aria-hidden="true"><circle class="qring-bg" cx="60" cy="60" r="52"></circle><circle class="qring-fg" cx="60" cy="60" r="52" style="stroke-dasharray:' + RING + ';stroke-dashoffset:' + RING + '" data-ring></circle></svg>';
    }

    function showInterstitial(next) {
      state.interShown = true;
      save();
      track('Quiz Interstitial Shown', { after: inter.after });
      showStage('<section class="qq"><h1>' + esc(inter.heading || 'Analysing…') + '</h1>' + (inter.body || '') + '</section>' +
        '<div class="qring"><div class="qring-box">' + ring() + '<div class="qring-num" data-pct>0%</div></div><div class="qring-status">' + esc(inter.status || 'Analysing') + '</div></div>');
      el.back.classList.add('off');
      var fg = el.screen.querySelector('[data-ring]'), pct = el.screen.querySelector('[data-pct]');
      var dur = inter.duration || 2000, t0 = null;
      fg.style.transition = 'none';
      (function frame(ts) {
        if (!t0) t0 = ts;
        var p = Math.min((ts - t0) / dur, 1), e = 1 - Math.pow(1 - p, 2);
        pct.textContent = Math.round(e * 100) + '%';
        fg.style.strokeDashoffset = (RING * (1 - e)).toFixed(1);
        if (p < 1) requestAnimationFrame(frame); else next();
      })(performance.now());
    }

    function showFinalLoader(next) {
      track('Quiz Final Loader Shown', {});
      var steps = (inter.steps || []).filter(Boolean);
      if (!steps.length) steps = ['Reading your answers', 'Matching products', 'Match found'];
      var dur = inter.finalDuration || 4000, per = dur / steps.length;
      showStage('<section class="qq"><h1>' + esc(inter.finalHeading || 'Building your match…') + '</h1></section><ol class="qload">' +
        steps.map(function (s) { return '<li><i></i><span>' + esc(s) + '</span></li>'; }).join('') + '</ol>');
      el.back.classList.add('off');
      var items = el.screen.querySelectorAll('.qload li');
      Array.prototype.forEach.call(items, function (li, i) {
        setTimeout(function () {
          li.classList.add('on');
          if (i > 0) { items[i - 1].classList.remove('on'); items[i - 1].classList.add('done'); }
          if (i === items.length - 1) setTimeout(function () { li.classList.remove('on'); li.classList.add('done'); }, per * 0.6);
        }, i * per);
      });
      setTimeout(next, dur + 350);
    }

    // ------------------------------------------------------------ scored result
    function showScored(next) {
      var sc = derive().score, overall = sc.overall;
      // A section can pin the verdict to the top tier; the shown score moves into that band too, so the ring and the words agree.
      if (scored.verdictForce === 'high') overall = Math.min(96, Math.max(overall, 75 + Math.round(overall * 0.2)));
      var level = overall >= 75 ? 'Severe' : overall >= 45 ? 'Moderate' : 'Mild';
      var tiers = scored.tiers || {};
      var tier = (overall >= 75 ? tiers.high : overall >= 45 ? tiers.mid : tiers.low) || {};
      var bars = [];
      (scored.barNames || []).forEach(function (name, i) {
        if (name) bars.push({ name: name, val: sc.bars[i] || 0, note: (scored.barNotes || [])[i] || '', diagnosis: (scored.barDiagnoses || [])[i] || '' });
      });
      var best = -1, topBar = null;
      state.gap = '';
      bars.forEach(function (b) { if (b.val > best) { best = b.val; state.gap = b.name.toLowerCase(); topBar = b; } });
      state.verdict = tier.verdict || '';
      state.score = overall;
      state.level = level;
      save();
      track('Quiz Results Shown', { verdict: state.verdict, score: overall, level: level });

      // One verdict, read top to bottom: the score in the ring, the diagnosis in big words (the top
      // bar's, else the section's heading, else the tier's verdict), where it sits on the three-word
      // scale, one sentence that counts what the answers exposed, the line that says the assessment
      // is complete, then the button. The chips, the bars and "what this means" follow as the
      // detail, with the button again under them, when the section shows them.
      var levels = String(scored.levels || 'High|Medium|Low').split('|');
      var lvl = function (v) { return v >= 70 ? (levels[0] || '') : v >= 40 ? (levels[1] || '') : (levels[2] || ''); };
      var sorted = bars.slice().sort(function (a, b) { return b.val - a.val; });
      var levelWords = String(scored.levelWords || 'Severe|Moderate|Mild').split('|').map(function (w) { return w.trim(); });
      var levelIdx = overall >= 75 ? 0 : overall >= 45 ? 1 : 2, severe = levelIdx === 0, mild = levelIdx === 2;
      // the blockers their answers exposed: the outcome chips they matched, else the bars reading medium or worse
      var told = outcomeMatches().filter(function (o) { return o.chip && String(o.q || '').trim().toLowerCase() !== 'gap'; }).slice(0, 4);
      var count = told.length || bars.filter(function (b) { return b.val >= 40; }).length || 1;
      var extra = { count: count };
      var headline = topBar && topBar.diagnosis ? fill(topBar.diagnosis, extra) : scored.heading ? fill(scored.heading, extra) : fill(tier.verdict || '', extra);
      var toldHtml = scored.showTold && told.length ? '<div class="qtold"><b>' + esc(scored.toldTitle || 'What you told us') + '</b><div>' + told.map(function (o) { return '<span>' + esc(subReveal(o.chip)) + '</span>'; }).join('') + '</div></div>' : '';
      var scaleHtml = '<div class="qscale" role="img" aria-label="' + esc(levelWords[levelIdx] || '') + '">' + levelWords.map(function (w, i) { return '<span' + (i === levelIdx ? ' class="on"' : '') + '>' + esc(w) + '</span>'; }).join('') + '</div>';
      var ctaHtml = function (end) { return '<div class="qnav"><button type="button" class="btn big" data-results-cta' + (end ? ' data-results-cta-end' : '') + '><b>' + esc(scored.cta || 'See my matches') + '</b>' + (scored.ctaNote ? '<small>' + esc(fill(scored.ctaNote, extra)) + '</small>' : '') + '</button></div>'; };
      var detailHtml = toldHtml +
        (scored.showBars !== false && sorted.length ? '<div class="qbars' + (severe ? ' severe' : '') + '"><b>' + esc(scored.gaugeLabel || '') + '</b>' + sorted.map(function (b, i) {
          return '<div class="qbar' + (i === 0 ? ' top' : '') + '"><div class="qbar-h"><span>' + esc(b.name) + (i === 0 && scored.topLabel ? ' <em class="qtag">' + esc(scored.topLabel) + '</em>' : '') + '</span><span>' + esc(lvl(b.val)) + '</span></div><div class="prog"><i data-bar="' + b.val + '"></i></div>' + (b.note ? '<small>' + esc(b.note) + '</small>' : '') + '</div>';
        }).join('') + '</div>' : '') +
        (scored.showMeans !== false && scored.means ? '<div class="grey"><h2>' + esc(scored.meansTitle || 'What this means') + '</h2>' + fill(scored.means, extra) + '</div>' : '');
      showStage('<section class="res qscore qclear' + (severe ? ' severe' : mild ? ' mild' : '') + '">' +
        (scored.eyebrow ? '<div class="eyebrow">' + esc(fill(scored.eyebrow, extra)) + '</div>' : '') +
        '<div class="qgauge">' + ring() + '<div class="qgauge-num"><span data-count>0</span><small>/100</small></div></div>' +
        '<h1>' + esc(headline) + '</h1>' + scaleHtml +
        (tier.summary ? '<div class="qsum">' + fill(tier.summary, extra) + '</div>' : '') +
        (scored.meta ? '<div class="qmeta">' + esc(fill(scored.meta, extra)) + '</div>' : '') +
        '</section>' + ctaHtml(false) +
        (detailHtml ? '<div class="qdetail">' + detailHtml + '</div>' + ctaHtml(true) : ''));
      el.back.classList.add('off');

      var fg = el.screen.querySelector('[data-ring]'), count_el = el.screen.querySelector('[data-count]');
      setTimeout(function () { fg.style.strokeDashoffset = (RING * (1 - overall / 100)).toFixed(1); }, 200);
      var t0 = null;
      (function frame(ts) {
        if (!t0) t0 = ts;
        var p = Math.min((ts - t0) / 1400, 1), e = 1 - Math.pow(1 - p, 3);
        count_el.textContent = Math.round(e * overall);
        if (p < 1) requestAnimationFrame(frame);
      })(performance.now());
      setTimeout(function () {
        Array.prototype.forEach.call(el.screen.querySelectorAll('[data-bar]'), function (i) { i.style.width = i.getAttribute('data-bar') + '%'; });
      }, 300);
      Array.prototype.forEach.call(el.screen.querySelectorAll('[data-results-cta]'), function (b) {
        b.addEventListener('click', function () {
          track('Quiz Results CTA Clicked', { score: overall, end: b.hasAttribute('data-results-cta-end') });
          next();
        });
      });
    }

    // ------------------------------------------------------------ email / SMS
    function emailForm() {
      var e = cap.email || {};
      return '<form class="qform" data-email-form novalidate><input type="email" class="qinput" name="email" placeholder="' + esc(e.placeholder || 'you@email.com') + '" autocomplete="email" required>' +
        '<button type="submit" class="btn">' + esc(e.cta || 'Unlock my discount') + '</button><p class="qerr" data-err hidden>' + esc(e.error || 'Please enter a valid email.') + '</p>' +
        (e.rule ? '<p class="note">' + esc(e.rule) + '</p>' : '') + '</form>';
    }

    function wireEmailForm(scope, done) {
      var form = scope.querySelector('[data-email-form]');
      if (!form) return;
      var input = form.querySelector('input'), err = form.querySelector('[data-err]');
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        var v = (input.value || '').trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { err.hidden = false; input.focus(); return; }
        err.hidden = true;
        captureEmail(v);
        done();
      });
      setTimeout(function () { try { input.focus({ preventScroll: true }); } catch (e) { /* no-op */ } }, 50);
    }

    function smsForm() {
      var s = cap.sms || {};
      return '<form class="qform" data-sms-form novalidate><input type="tel" class="qinput" name="phone" placeholder="' + esc(s.placeholder || '') + '" autocomplete="tel">' +
        '<button type="submit" class="btn">' + esc(s.cta || 'Double my discount') + '</button><p class="qerr" data-err hidden>' + esc(s.error || 'Please enter a valid mobile number.') + '</p>' +
        '<button type="button" class="qlink" data-sms-skip>' + esc(s.skip || 'No thanks') + '</button>' +
        (s.rule ? '<p class="note">' + esc(s.rule) + '</p>' : '') + '</form>';
    }

    function wireSmsForm(scope, done) {
      var form = scope.querySelector('[data-sms-form]');
      if (!form) return;
      var input = form.querySelector('input'), err = form.querySelector('[data-err]');
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        var v = (input.value || '').trim();
        if (v.replace(/[^0-9]/g, '').length < 8) { err.hidden = false; input.focus(); return; }
        err.hidden = true;
        capturePhone(v);
        done(true);
      });
      form.querySelector('[data-sms-skip]').addEventListener('click', function () {
        state.smsSkipped = true;
        save();
        done(false);
      });
      setTimeout(function () { try { input.focus({ preventScroll: true }); } catch (e) { /* no-op */ } }, 50);
    }

    function captureEmail(email) {
      state.email = email;
      state.discount = ds.discountCode || '';
      save();
      track('Quiz Email Captured', { email: email });
      klaviyoSubscribe(email, null, 'email');
      klaviyoIdentify();
      applyDiscount();
    }

    function capturePhone(phone) {
      state.phone = phone;
      state.discount = ds.discountCodeSms || ds.discountCode || '';
      state.doubled = true;
      save();
      track('Quiz SMS Captured', { email: state.email, phone: phone });
      klaviyoSubscribe(state.email, phone, 'email_sms');
      klaviyoIdentify();
      applyDiscount();
    }

    // Gate mode: email screen, then the SMS upsell, then the matches.
    function showGate(next) {
      var e = cap.email || {};
      showStage('<section class="qq"><h1>' + esc(fill(e.headline || 'Your match is ready')) + '</h1>' + (e.body || '') + '</section>' + emailForm());
      el.back.classList.remove('off');
      wireEmailForm(el.screen, function () {
        // no SMS code means no upsell, so nothing was doubled: Quiz Completed used to say it was
        if (ds.discountCodeSms) showSms(next); else next(false);
      });
    }

    function showSms(next) {
      var e = cap.email || {}, s = cap.sms || {};
      showStage('<section class="qq">' + (e.badge ? '<span class="pill">' + esc(e.badge) + '</span>' : '') + '<h1>' + esc(s.headline || '') + '</h1>' + (s.body || '') + '</section>' + smsForm());
      el.back.classList.add('off');
      wireSmsForm(el.screen, function (doubled) { next(doubled); });
    }

    // The answers, the result and the match as profile properties, every key prefixed with the
    // quiz id, so two quizzes on the site never write over each other's properties.
    function profileProps() {
      var p = { last_quiz_taken: quizId };
      Object.keys(state.answers).forEach(function (k) { p[quizId + '_' + k] = state.answers[k]; });
      var d = derive();
      if (d.match) p[quizId + '_match'] = d.match;
      if (state.gap) p[quizId + '_gap'] = state.gap;
      if (state.score != null) p[quizId + '_score'] = state.score;
      if (state.level) p[quizId + '_level'] = state.level;
      if (state.verdict) p[quizId + '_verdict'] = state.verdict;
      return p;
    }

    // Klaviyo: list subscribe with the answers as profile properties, then
    // identify so the on-site profile carries them too. The consent is
    // implied at this API revision (2024-10-15): Klaviyo subscribes the
    // address to email marketing and a number, when one is sent, to SMS, and
    // takes no "subscriptions" object anywhere; one sat beside the profile
    // here from September to 27 Oct 2026 and every quiz sign-up was turned
    // away for it (docs/hx-klaviyo-signups.md). A request Klaviyo refuses is
    // logged with its reason and sent once more with the address alone, so a
    // field it will not take costs the answers, not the sign-up.
    function klaviyoSubscribe(email, phone, kind) {
      var pub = ds.klaviyoPublic, list = ds.klaviyoList || '', smsList = ds.klaviyoSmsList || list;
      if (!pub || !email) return;
      var props = profileProps();
      function send(listId, attrs) {
        return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(pub), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({ data: { type: 'subscription', attributes: { custom_source: 'Quiz: ' + quizId, profile: { data: { type: 'profile', attributes: attrs } } }, relationships: { list: { data: { type: 'list', id: listId } } } } })
        });
      }
      function reason(r) {
        return r.text().then(function (t) {
          var detail = '';
          try { detail = JSON.parse(t).errors[0].detail || ''; } catch (e) { /* not its JSON */ }
          return 'Klaviyo answered ' + r.status + (detail ? ': ' + detail : '');
        }, function () { return 'Klaviyo answered ' + r.status; });
      }
      function sub(listId, withPhone) {
        if (!listId) return;
        var ph = withPhone ? e164(phone) : null;
        var attrs = { email: email, properties: props };
        if (ph) attrs.phone_number = ph;
        try {
          send(listId, attrs).then(function (r) {
            if (r.ok) return;
            return reason(r).then(function (why) {
              console.warn('Klaviyo quiz sign-up refused: ' + why + (r.status === 400 ? '; sending the address alone' : ''));
              if (r.status !== 400) return;
              var bare = { email: email };
              if (ph) bare.phone_number = ph;
              return send(listId, bare).then(function (r2) {
                if (!r2.ok) return reason(r2).then(function (why2) { console.warn('Klaviyo quiz sign-up refused: ' + why2); });
              });
            });
          }).catch(function () {});
        } catch (e) { /* no-op */ }
      }
      if (kind === 'email_sms') { sub(smsList, true); if (list && list !== smsList) sub(list, false); }
      else sub(list, false);
    }

    function klaviyoIdentify() {
      if (!state.email || !(window.klaviyo && typeof window.klaviyo.push === 'function')) return;
      var p = profileProps();
      p.$email = state.email;
      if (state.phone) p.$phone_number = state.phone;
      window.klaviyo.push(['identify', p]);
    }

    function cartAttributes() {
      var d = derive();
      try {
        fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          // offer mode has no match driver, so the match is the product the offer shows
          body: JSON.stringify({ attributes: { quiz: quizId, quiz_session: state.sid, quiz_gap: state.gap || '', quiz_match: d.match || (offer ? (picksFor(d)[0] || '') : ''), quiz_discount: state.discount || '' } })
        }).catch(function () {});
      } catch (e) { /* no-op */ }
    }

    // Shopify's share-a-discount link sets the checkout discount cookie; the
    // response is a redirect home, which fetch follows and drops.
    function applyDiscount() {
      if (!state.discount) return;
      try { fetch('/discount/' + encodeURIComponent(state.discount), { credentials: 'same-origin' }).catch(function () {}); } catch (e) { /* no-op */ }
      // The link above only sets a checkout cookie. Putting the code on the cart itself is what
      // makes the drawer, the buy box and checkout all show the saving before checkout.
      try {
        fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: state.discount }) })
          .then(cartAttributes, cartAttributes);
      } catch (e) { cartAttributes(); }
    }

    // ------------------------------------------------------------ finish → results
    function finish(doubled) {
      var d = derive();
      state.done = true;
      state.doubled = !!doubled || state.doubled;
      save();
      // offer mode has no capture step, so the code is applied here, on finishing the questions
      // offer mode and no-capture mode have no email step, so the code is applied here, on finishing the questions
      if ((offer || cap.mode === 'none') && !state.discount && ds.discountCode) { state.discount = ds.discountCode; applyDiscount(); }
      // the product the reveal (or the offer, or the picks) will show, named as Quiz Recommendations Shown names it
      var topHandle = revealOn ? (reveal.product || (finder.fallbackProducts || [])[0] || '') : (picksFor(d)[0] || '');
      track('Quiz Completed', {
        email: state.email || '', match: d.match || '', top_product: topHandle, count: Object.keys(state.answers).length,
        gap: state.gap || '', verdict: state.verdict || '', score: state.score != null ? state.score : '', level: state.level || '',
        doubled: state.doubled, discount: state.discount || ''
      });
      klaviyoIdentify();
      cartAttributes();
      showResults(true);
    }

    function picksFor(d) {
      var picks = rank(d.products, 3);
      if (!picks.length && (finder.fallbackProducts || []).length) picks = finder.fallbackProducts.slice(0, 3);
      if (!picks.length) picks = Object.keys(catalog).slice(0, 3);
      return picks.filter(function (h) { return catalog[h]; });
    }

    function variantFor(p, match) {
      if (match && p.variants && p.variants.length) {
        var m = String(match).toLowerCase();
        for (var i = 0; i < p.variants.length; i++) {
          var v = p.variants[i];
          if (v.available !== false && String(v.title || '').toLowerCase().indexOf(m) >= 0) return v;
        }
      }
      return null;
    }

    function priceOf(p) {
      var pct = pctNow(), base = +p.price || 0;
      var now = pct > 0 ? Math.round(base * (1 - pct / 100)) : base;
      var was = pct > 0 ? base : (p.compare > base ? p.compare : 0);
      return { now: now, was: was };
    }

    function showResults(announce) {
      if (offer) return showOffer(announce);
      if (revealOn) return showReveal(announce);
      var d = derive();
      var picks = picksFor(d);
      var topHandle = picks[0] || '';
      var top = catalog[topHandle];
      var topCol = rank(d.collections, 1)[0] || finder.defaultCollection || '';
      var col = topCol && collections[topCol] ? collections[topCol] : null;
      var vars = { product: top ? top.short : '', collection: col ? col.title : '', n: picks.length };
      var pct = pctNow();

      el.stage.hidden = true;
      el.results.hidden = false;
      paint();

      el.eyebrow.textContent = fill(finder.eyebrow || 'Your matches', vars);
      el.title.innerHTML = fw(fill(finder.title || 'Your top matches', vars));
      el.sub.textContent = fill(finder.sub || '', vars);

      // why: one line per pick from the answers that pointed to it
      var why = picks.map(function (h) {
        var r = uniq(d.reasons[h] || []);
        return '<div><span><b>' + esc(catalog[h].short) + '</b> ' + (r.length ? 'because you chose ' + esc(joinAnd(r)) : esc(finder.whyFallback || 'a solid all-rounder to start with')) + '</span></div>';
      });
      if (pct > 0) why.push('<div><span><b>' + pct + '% off</b> is applied at checkout with code ' + esc(state.discount) + '</span></div>');
      el.why.innerHTML = why.join('');

      // picks: rows + total
      var ids = [], sumNow = 0, sumWas = 0;
      el.picks.innerHTML = picks.map(function (h, i) {
        var p = catalog[h], v = i === 0 ? variantFor(p, d.match) : null, pr = priceOf(v ? { price: v.price, compare: 0 } : p);
        ids.push(v ? v.id : p.variantId);
        sumNow += pr.now; sumWas += pr.was || pr.now;
        var tag = i === 0 ? fill(finder.topLabel || 'Top match', vars) : fill(finder.pickLabel || 'Pick [n]', { n: i + 1 });
        return '<div class="it"><a class="im" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true">' + (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : '') + '</a>' +
          '<div><b><a href="' + esc(p.url) + '">' + esc(p.short) + (v ? ' · ' + esc(v.title) : '') + '</a></b><small>' + esc(money(pr.now)) + (pr.was ? ' <s>' + esc(money(pr.was)) + '</s>' : '') + (p.line ? ' · ' + esc(p.line) : '') + '</small></div>' +
          '<span class="pill tg' + (i ? ' g' : '') + '">' + esc(tag) + '</span></div>';
      }).join('') + (picks.length ? '<div class="sum"><span>' + picks.length + (picks.length === 1 ? ' product' : ' products') + (pct > 0 ? ' · ' + pct + '% off applied' : '') + '</span><span><b>' + esc(money(sumNow)) + '</b>' + (sumWas > sumNow ? '<s>' + esc(money(sumWas)) + '</s>' : '') + '</span></div>' : '');

      // actions
      el.actions.innerHTML = top
        ? '<button type="button" class="btn" data-atc="' + esc(ids.join(',')) + '">' + esc(fill(picks.length > 1 ? (finder.atcAll || 'Add all [n] to cart') : (finder.atcOne || 'Add to cart'), vars)) + '</button>' +
          '<a class="qlink" href="' + esc(top.url) + '">' + esc(fill(finder.see || 'See [product]', vars)) + '</a>'
        : '';

      // single-product path when there is more than one pick
      if (top && picks.length > 1) {
        el.single.hidden = false;
        el.single.innerHTML = '<h2>' + esc(fill(finder.singleTitle || 'Or start with one', vars)) + '</h2><p>' + esc(fill(finder.singleBody || '', vars)) + '</p>' +
          '<button type="button" class="btn" data-atc="' + esc(ids[0]) + '">' + esc(fill(finder.singleCta || 'Add [product] to cart', vars)) + '</button>';
      } else {
        el.single.hidden = true;
        el.single.innerHTML = '';
      }

      renderCapture();

      // alternatives: next-ranked products, then the rest of the catalog
      var alts = rank(d.products, 20).concat(Object.keys(catalog)).filter(function (h, i, a) {
        return catalog[h] && picks.indexOf(h) < 0 && a.indexOf(h) === i;
      }).slice(0, 2);
      if (alts.length || col) {
        el.alts.hidden = false;
        el.alts.innerHTML = '<h2>' + esc(fill(finder.altsLabel || 'Also good for you', vars)) + '</h2>' + alts.map(function (h) {
          var p = catalog[h], pr = priceOf(p);
          return '<a class="row" href="' + esc(p.url) + '">' + (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : '') + '<div class="t"><b>' + esc(p.short) + '</b><small>' + esc(p.line || '') + '</small></div><span class="p">' + esc(money(pr.now)) + '</span></a>';
        }).join('') + (col ? '<a class="qlink" href="' + esc(col.url) + '">' + esc(fill(finder.collectionCta || 'Shop all [collection]', vars)) + '</a>' : '');
      } else {
        el.alts.hidden = true;
      }

      // server-rendered copy with {gap} / {verdict} / {name}
      Array.prototype.forEach.call(el.results.querySelectorAll('[data-fill]'), function (node) {
        if (node.__tpl == null) node.__tpl = node.textContent;
        node.textContent = fill(node.__tpl, vars);
      });

      wireAtc(el.results);
      if (announce) track('Quiz Recommendations Shown', { top_product: topHandle, top_collection: topCol || '' });
      toTop();
    }

    // ------------------------------------------------------------ reveal mode
    // The section's reveal setting: after the result the stage shows a reveal
    // card (timer, heading, why line, the code) and the page's own sections
    // after this one unfold below it; the section's style keeps them hidden
    // until now. For a quiz that lands on one product whose real product page
    // sits under it, so nothing is a copy of the page.
    function subReveal(text, extra) {
      if (text == null) return '';
      var d = derive(), idx = -1;
      (scored.barNames || []).forEach(function (n, i) { if (n && String(n).toLowerCase() === state.gap) idx = i; });
      var problem = state.gap || reveal.problemFallback || '';
      var reason = String((reveal.reasons || [])[idx] || '').replace(/\{problem\}/gi, problem);
      var out = String(text).replace(/\{reason\}/gi, reason).replace(/\{problem\}/gi, problem)
        .replace(/\[code\]/gi, state.discount || '').replace(/\[pct\]/gi, String(pctNow() || ''));
      // {first}: the first name with no comma logic, so "{first}'s match" reads "Sam's match", or "Your match" with no name
      out = d.name ? out.replace(/\{first\}/gi, d.name) : out.replace(/\{first\}['\u2019]s/gi, 'Your').replace(/\{first\}/gi, 'you');
      out = fill(out, extra);
      if (!d.name) out = out.replace(/^\s*([a-z])/, function (_, c) { return c.toUpperCase(); });
      return out;
    }

    // Outcome copy blocks: each carries a condition (an answer, or the highest bar) and copy.
    // Every block that matches contributes its chip, why bullet, plan line and week overrides.
    function outcomeMatches() {
      var list = cfg.outcomes || [], hit = [], gapIdx = -1;
      (scored.barNames || []).forEach(function (n, i) { if (n && String(n).toLowerCase() === state.gap) gapIdx = i; });
      list.forEach(function (o) {
        var q = String(o.q || '').trim().toLowerCase(), want = String(o.opt || '').trim().toLowerCase();
        if (!q || !want) return;
        if (q === 'gap') {
          if (gapIdx >= 0 && (want === String(gapIdx + 1) || want === String(scored.barNames[gapIdx] || '').toLowerCase())) hit.push(o);
          return;
        }
        var lab = String(state.labels[q] == null ? '' : state.labels[q]).trim().toLowerCase(), val = String(state.answers[q] == null ? '' : state.answers[q]).toLowerCase();
        if ((lab && lab === want) || (val && val === want)) hit.push(o);
      });
      hit.sort(function (a, b) { return (+a.priority || 5) - (+b.priority || 5); });
      return hit;
    }

    // The plan's price for its tub count, from the buy box on the page: the single-tub price
    // times the count, less the ladder tier the count reaches (the automatic discount's percent).
    function planOffer(qty) {
      var pdp = document.getElementById('hx-pdp');
      if (!pdp) return null;
      var unit = +pdp.getAttribute('data-price') || 0;
      if (!unit || !qty) return null;
      var qd = [];
      try { qd = JSON.parse(pdp.getAttribute('data-qd') || '[]') || []; } catch (e) { qd = []; }
      var pct = 0;
      qd.forEach(function (t) { if (+t.quantity <= qty && +t.discount > pct) pct = +t.discount; });
      var was = unit * qty, now = Math.round(was * (1 - pct / 100));
      var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD';
      var hxSym = document.documentElement.dataset.sym; // outside Australia: the market's symbol from <html> (US$, NZ$, £)
      var money = function (c) { if (hxSym) return hxSym + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); try { return new Intl.NumberFormat('en-AU', { style: 'currency', currency: cur }).format(c / 100); } catch (e) { return '$' + (c / 100).toFixed(2); } };
      return { now: money(now), was: pct ? money(was) : '', pct: pct };
    }

    // Scroll an element to the top of the view, under the page's sticky bar when it has one.
    function scrollToEl(n) {
      if (!n) return;
      var bar = document.querySelector('.hx-mini'), off = bar ? bar.getBoundingClientRect().height : 0;
      window.scrollTo({ top: n.getBoundingClientRect().top + window.pageYOffset - off, behavior: 'smooth' });
    }

    function clockText(ms) {
      var s = Math.max(0, Math.round(ms / 1000)), m = Math.floor(s / 60), r = s % 60;
      return (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r;
    }

    function showReveal(announce) {
      var handle = reveal.product || (finder.fallbackProducts || [])[0] || '';
      var top = catalog[handle];
      var vars = { product: top ? top.short : '', collection: '', n: 1 };
      if (!state.revealAt) { state.revealAt = Date.now(); save(); }
      var mins = +reveal.timerMinutes || 0, code = state.discount;
      var tl = cfg.timeline || {}, weeks = [];
      for (var wi = 0; wi + 1 < (tl.weeks || []).length; wi += 2) if (tl.weeks[wi + 1]) weeks.push({ label: tl.weeks[wi], body: tl.weeks[wi + 1] });
      var hits = outcomeMatches();
      var gapWeeks = null;
      hits.forEach(function (o) { if (!gapWeeks && String(o.q || '').trim().toLowerCase() === 'gap' && (o.weeks || []).some(Boolean)) gapWeeks = o.weeks; });
      if (gapWeeks) weeks = weeks.map(function (w, i) { return { label: w.label, body: gapWeeks[i] || w.body }; });
      var tlHtml = tl.enabled && weeks.length ? '<h2>' + esc(subReveal(tl.title || '', vars)) + '</h2>' + (tl.intro ? '<p>' + esc(subReveal(tl.intro, vars)) + '</p>' : '') +
        '<ol>' + weeks.map(function (w) { return '<li><b>' + esc(w.label) + '</b><span>' + esc(subReveal(w.body, vars)) + '</span></li>'; }).join('') + '</ol>' +
        (tl.disclaimer ? '<p class="note">' + esc(tl.disclaimer) + '</p>' : '') : '';
      var slot = document.querySelector('[data-quiz-timeline-slot]');
      var chips = hits.filter(function (o) { return o.chip; });
      var whys = hits.filter(function (o) { return o.why; }).slice(0, 3);
      var plans = hits.filter(function (o) { return o.plan; }).sort(function (a, b) { return (+a.planOrder || 5) - (+b.planOrder || 5); });
      var toldHtml = chips.length ? '<div class="qchips"><span class="qchips-t">' + esc(subReveal(reveal.toldTitle || 'What you told us', vars)) + '</span>' + chips.map(function (o) { return '<span class="qchip">' + esc(subReveal(o.chip, vars)) + '</span>'; }).join('') + '</div>' : '';
      var whyHtml = whys.length ? '<div class="qwhy"><h2>' + esc(subReveal(reveal.whyTitle || 'Why this is your match', vars)) + '</h2><ul>' + whys.map(function (o) { return '<li>' + esc(subReveal(o.why, vars)) + '</li>'; }).join('') + '</ul></div>' : '';
      // the plan reads as the best offer: tag, title, the tub count's price against the single-tub
      // price (from the buy box on the page), the closing line, the dosing lines, the ticks, the button
      var offer = planOffer(+reveal.planQty || 1);
      var perks = String(reveal.planPerks || '').split(/\r?\n/).map(function (t) { return t.trim(); }).filter(Boolean);
      var planHtml = reveal.planEnabled !== false && plans.length ? '<section class="qplan">' +
        (reveal.planTag ? '<span class="eb">' + esc(subReveal(reveal.planTag, vars)) + '</span>' : '') +
        '<h2>' + esc(subReveal(reveal.planTitle || 'Your plan', vars)) + '</h2>' +
        (offer ? '<div class="saving"><span class="now">' + esc(offer.now) + '</span>' + (offer.was ? '<span class="was">' + esc(offer.was) + '</span>' : '') + (offer.pct ? '<span class="cut">Save ' + offer.pct + '%</span>' : '') + '</div>' : '') +
        (reveal.planClose ? '<p class="because">' + esc(subReveal(reveal.planClose, vars)) + '</p>' : '') +
        '<ul>' + plans.map(function (o) { return '<li>' + esc(subReveal(o.plan, vars)) + '</li>'; }).join('') + '</ul>' +
        (perks.length ? '<div class="gets">' + perks.map(function (t) { return '<span class="get">' + esc(subReveal(t, vars)) + '</span>'; }).join('') + '</div>' : '') +
        (reveal.planCta ? '<button type="button" class="btn" data-plan-qty="' + (+reveal.planQty || 1) + '">' + esc(subReveal(reveal.planCta, vars)) + '</button>' : '') + '</section>' : '';
      // The match card: the product itself (image, name, rating, the price with the code), the
      // reasons, the button and the code. The quiz's catalog entry for the reveal product supplies
      // the image, name and price; the buy box on the page supplies the rating line.
      var mp = catalog[handle] || null, bx = document.getElementById('hx-pdp'), stage = +reveal.stageSections || 0;
      var starsEl = bx && bx.querySelector('.stars'), starsTxt = starsEl ? (starsEl.textContent.split('Reviewed')[0] || '').replace(/\s+/g, ' ').trim() : '';
      var sm = starsTxt.match(/^(\u2605+)\s*(.*)$/), starsHtml = starsTxt ? (sm ? '<span class="s">' + sm[1] + '</span> ' + esc(sm[2]) : esc(starsTxt)) : '';
      var heroEl = bx && bx.querySelector('#heroImg'), mImg = (mp && mp.image) || (heroEl && heroEl.getAttribute('src')) || '';
      var titleEl = bx && bx.querySelector('.title h1, .title h2'), mTitle = (mp && mp.short) || (titleEl ? titleEl.textContent.trim() : '');
      var mPrice = mp && mp.price ? priceOf(mp) : null, pctOff = pctNow();
      // no tick before the state line: it asks for the code to be applied at checkout rather than claiming it was
      var codeHtml = code ? '<div class="qcode"><div class="qcode-box"><span class="qcode-code">' + esc(code) + '</span><span class="qcode-state">' + esc(subReveal(reveal.codeLabel || 'Apply it at checkout to save [pct]%', vars)) + '</span></div>' +
          '<button type="button" class="btn qcopy" data-reveal-copy>' + esc(reveal.copyLabel || 'Copy') + '</button></div>' +
          (reveal.codeNote ? '<p class="note qcode-note">' + esc(subReveal(reveal.codeNote, vars)) + '</p>' : '') : '';
      var ctaHtml = reveal.cta ? '<button type="button" class="btn" data-reveal-cta="card">' + esc(subReveal(reveal.cta, vars)) + '</button>' : '';
      var matchHtml = (mTitle || mImg) ? '<section class="qmc">' +
        (mImg ? '<img class="qmc-img" src="' + esc(mImg) + '" alt="" width="320" height="320" loading="eager">' : '') + '<div class="qmc-info">' +
          (reveal.matchLabel ? '<span class="eb">' + esc(subReveal(reveal.matchLabel, vars)) + '</span>' : '') +
          (mTitle ? '<h2>' + esc(mTitle) + '</h2>' : '') +
          (starsHtml ? '<div class="qmc-stars">' + starsHtml + '</div>' : '') +
          (mPrice ? '<div class="qmc-price"><b>' + esc(money(mPrice.now)) + '</b>' + (mPrice.was ? '<s>' + esc(money(mPrice.was)) + '</s>' : '') + (pctOff > 0 ? '<span class="qmc-off">' + pctOff + '% off</span>' : '') + '</div>' : '') +
        '</div>' + whyHtml + ctaHtml + codeHtml + '</section>' : whyHtml + ctaHtml + codeHtml;
      // the closing button only earns its place under the sections of a two-stage reveal
      var endHtml = reveal.cta && (stage > 0 || !matchHtml) ? '<div class="qnav"><button type="button" class="btn" data-reveal-cta="end">' + esc(subReveal(reveal.cta, vars)) + '</button></div>' : '';
      var html = '<section class="qreveal">' +
        (mins > 0 ? '<div class="qtimer" data-reveal-timer><span class="qtimer-main"><span data-reveal-label>' + esc(subReveal(reveal.timerLabel, vars)) + '</span><b data-reveal-clock>' + clockText(state.revealAt + mins * 60000 - Date.now()) + '</b></span></div>' : '') +
        (reveal.eyebrow ? '<p class="qeb">' + esc(subReveal(reveal.eyebrow, vars)) + '</p>' : '') +
        '<h1>' + esc(subReveal(reveal.heading, vars)) + '</h1>' +
        (reveal.because ? '<p class="qlead">' + esc(subReveal(reveal.because, vars)) + '</p>' : '') +
        (reveal.showChips === false ? '' : toldHtml) +
        matchHtml + endHtml +
        (!slot ? planHtml + (tlHtml ? '<section class="qtl">' + tlHtml + '</section>' : '') : '') +
        '</section>';
      // the plan and the timeline go into an hx-quiz-timeline slot when the page has one (under the product), else into the card
      if (slot) { slot.innerHTML = planHtml + (tlHtml ? '<section class="qtl">' + tlHtml + '</section>' : ''); slot.hidden = !(planHtml || tlHtml); }
      showStage(html);
      el.back.classList.add('off');

      // the product sections after this one
      var sec = root.closest('.shopify-section');
      if (sec) sec.classList.add('hxq-revealed');
      try { window.dispatchEvent(new Event('resize')); } catch (e) { /* no-op */ }

      // Two stages when the section asks for it: the first N sections after the quiz show with the
      // card (the section's own style hides the rest), the button moves under the Nth, and pressing
      // it shows everything else. The button keeps the quiz's classes through the .hxq wrapper.
      var endCta = el.screen.querySelector('[data-reveal-cta="end"]'), claimWrap = null;
      if (stage > 0 && sec && endCta) {
        var nav = endCta.closest('.qnav') || endCta, after = null, seen = 0, sib = sec.nextElementSibling;
        while (sib && seen < stage) { if (sib.classList.contains('shopify-section')) { after = sib; seen++; } sib = sib.nextElementSibling; }
        if (after) {
          claimWrap = document.createElement('div');
          claimWrap.className = 'hxq hxq-claim';
          claimWrap.appendChild(nav);
          after.parentNode.insertBefore(claimWrap, after.nextSibling);
        }
      }

      // a tag above the product title in the buy box, and the plan button that sets its tub count
      var pdp = document.getElementById('hx-pdp'), buy = document.querySelector('#buy, .buy') || pdp;
      if (pdp && reveal.matchTag && !pdp.querySelector('.qmatch')) {
        var title = pdp.querySelector('.title h1, .title h2');
        if (title) { var tag = document.createElement('div'); tag.className = 'qmatch'; tag.textContent = subReveal(reveal.matchTag, vars); title.parentNode.insertBefore(tag, title); }
      }
      Array.prototype.forEach.call(document.querySelectorAll('[data-plan-qty]'), function (b) {
        b.addEventListener('click', function () {
          var want = +b.getAttribute('data-plan-qty') || 1, out = document.getElementById('qtyOut'), plus = document.getElementById('plus'), guard = 0;
          while (out && plus && (+out.textContent || 1) < want && guard++ < 8) plus.click();
          track('Quiz Plan Tubs Set', { qty: want });
          scrollToEl(buy);
        });
      });

      var clock = el.screen.querySelector('[data-reveal-clock]'), label = el.screen.querySelector('[data-reveal-label]'), timer = el.screen.querySelector('[data-reveal-timer]');
      if (clock) (function tick() {
        if (!clock.isConnected) return;
        var left = state.revealAt + mins * 60000 - Date.now();
        clock.textContent = clockText(left);
        if (timer) timer.classList.toggle('late', left <= 120000);   // the last two minutes read as urgent
        if (left <= 0) { if (label && reveal.timerDone) label.textContent = subReveal(reveal.timerDone, vars); return; }
        setTimeout(tick, 1000);
      })();

      function wireCopy(btn) {
        if (!btn) return;
        btn.addEventListener('click', function () {
          var done = function () { btn.textContent = reveal.copiedLabel || 'Copied'; };
          try { navigator.clipboard.writeText(state.discount).then(done, done); } catch (e) { done(); }
          track('Quiz Code Copied', { discount: state.discount || '' });
        });
      }
      wireCopy(el.screen.querySelector('[data-reveal-copy]'));

      // The buy box under the result: the code's percentage goes onto its prices (hx-pdp.js reads
      // data-code-pct and data-code on #hx-pdp and re-prices) and the same code block lands in the
      // slot the buy box keeps under Add to cart.
      var pdp = document.getElementById('hx-pdp'), slot = document.querySelector('[data-quiz-code-slot]');
      if (pdp) {
        if (code && pctOff > 0) { pdp.setAttribute('data-code-pct', String(pctOff)); pdp.setAttribute('data-code', code); }
        else { pdp.removeAttribute('data-code-pct'); pdp.removeAttribute('data-code'); }
        try { document.dispatchEvent(new CustomEvent('hx:code', { detail: { code: code || '', pct: pctOff } })); } catch (e) { /* no-op */ }
      }
      if (slot) {
        if (codeHtml) { slot.innerHTML = codeHtml; slot.hidden = false; wireCopy(slot.querySelector('[data-reveal-copy]')); }
        else { slot.innerHTML = ''; slot.hidden = true; }
      }

      // Two pages. claimPage opens the product page: the result and the sections shown with it go,
      // the timer strip moves into the wrapper (which sits where the product page starts) and sticks
      // under the page's bar, still counting, with a way back inside it, and the page starts from the
      // top. backToResult reverses every step. The claim pushes a history entry, so the browser's
      // back button returns to the result too.
      var endNav = null, pushed = false;
      function claimPage(fromHistory) {
        if (!claimWrap || !sec || sec.classList.contains('hxq-claimed')) return;
        sec.classList.add('hxq-claimed');
        var timerEl = el.screen.querySelector('[data-reveal-timer]'), s1 = sec.nextElementSibling;
        while (s1 && s1 !== claimWrap) { if (s1.classList.contains('shopify-section')) s1.style.display = 'none'; s1 = s1.nextElementSibling; }
        sec.style.display = 'none';
        endNav = claimWrap.firstElementChild;
        if (endNav) endNav.parentNode.removeChild(endNav);
        if (timerEl) {
          var bar = document.querySelector('.hx-mini');
          claimWrap.className = 'hxq hxq-timerbar';
          claimWrap.style.top = (bar ? Math.round(bar.getBoundingClientRect().height) : 0) + 'px';
          claimWrap.appendChild(timerEl);
          if (reveal.backLabel) {
            var back = document.createElement('button');
            back.type = 'button';
            back.className = 'qtimer-back';
            back.setAttribute('data-reveal-back', '');
            back.textContent = '\u2039 ' + reveal.backLabel;
            back.addEventListener('click', function () {
              if (pushed && window.history && history.state && history.state.hxq === 'claimed') history.back(); else backToResult();
            });
            timerEl.insertBefore(back, timerEl.firstChild);
          }
        } else {
          claimWrap.hidden = true;
        }
        if (!fromHistory && window.history && history.pushState) { try { history.pushState({ hxq: 'claimed' }, ''); pushed = true; } catch (e) { pushed = false; } }
        try { window.dispatchEvent(new Event('resize')); } catch (e) { /* no-op */ }
        track('Quiz Match Claimed', { top_product: handle });
        window.scrollTo(0, 0);
      }
      function backToResult() {
        if (!claimWrap || !sec || !sec.classList.contains('hxq-claimed')) return;
        var timerEl = claimWrap.querySelector('[data-reveal-timer]'), back = claimWrap.querySelector('[data-reveal-back]'), card = el.screen.querySelector('.qreveal');
        if (back) back.parentNode.removeChild(back);
        if (timerEl && card) card.insertBefore(timerEl, card.firstChild);
        claimWrap.className = 'hxq hxq-claim';
        claimWrap.style.top = '';
        claimWrap.hidden = false;
        if (endNav) claimWrap.appendChild(endNav);
        sec.style.display = '';
        var s1 = sec.nextElementSibling;
        while (s1 && s1 !== claimWrap) { if (s1.classList.contains('shopify-section')) s1.style.display = ''; s1 = s1.nextElementSibling; }
        sec.classList.remove('hxq-claimed');
        try { window.dispatchEvent(new Event('resize')); } catch (e) { /* no-op */ }
        track('Quiz Back To Result', { top_product: handle });
        window.scrollTo(0, 0);
      }
      if (claimWrap && window.history && history.replaceState) { try { history.replaceState({ hxq: 'result' }, ''); } catch (e) { /* no-op */ } }
      window.addEventListener('popstate', function (ev) {
        var st = ev.state || {};
        if (st.hxq === 'claimed') claimPage(true);
        else if (sec && sec.classList.contains('hxq-claimed')) backToResult();
      });

      Array.prototype.forEach.call(document.querySelectorAll('[data-reveal-cta]'), function (b) {
        b.addEventListener('click', function () {
          if (claimWrap && sec && !sec.classList.contains('hxq-claimed')) { claimPage(false); return; }
          // one page: the match is the product, so scroll to the buy box when the page has one, else to whatever follows the quiz
          var n = document.getElementById('hx-pdp') || (sec && sec.nextElementSibling);
          while (n && !(n.offsetWidth || n.offsetHeight)) n = n.nextElementSibling;
          scrollToEl(n);
        });
      });

      if (announce) track('Quiz Recommendations Shown', { top_product: handle, top_collection: '' });
      toTop();
    }

    // Optional mode: the discount box lives on the results; gate mode shows
    // the unlocked state there once captured.
    function renderCapture() {
      var box = el.capture, e = cap.email || {}, s = cap.sms || {};
      if (!box) return;
      if (cap.mode === 'none' || !ds.discountCode) { box.hidden = true; return; }
      if (!state.email) {
        if (cap.mode !== 'optional') { box.hidden = true; return; }
        box.hidden = false;
        box.innerHTML = '<h2>' + esc(fill(e.headline || '')) + '</h2>' + (e.body || '') + emailForm();
        wireEmailForm(box, function () { showResults(false); });
        return;
      }
      if (!state.phone && !state.smsSkipped && ds.discountCodeSms) {
        box.hidden = false;
        box.innerHTML = (e.badge ? '<span class="pill">' + esc(e.badge) + '</span>' : '') + '<h2>' + esc(s.headline || '') + '</h2>' + (s.body || '') + smsForm();
        wireSmsForm(box, function () { showResults(false); });
        return;
      }
      box.hidden = false;
      box.innerHTML = '<span class="pill">' + esc(pctNow() + '% off unlocked') + '</span><p>' + esc(cap.done || 'Your code is applied at checkout.') + '</p>';
    }

    // ------------------------------------------------------------ offer mode
    // sections/hx-quiz-offer. After the questions (and the loader) the top
    // match is shown as a buy box rather than a list: the product, its
    // flavours as a dropdown, the tub tiers from the section's tier blocks,
    // the gift rows from its gift blocks, the code already applied, and add to
    // cart. Gifts are added with the _lp_gift stamps the buy box uses, so the
    // cart drawer sweeps them the same way. Answers can also carry a tub count
    // (qty) and a one-line note; the last answered wins.
    var offerUI = { qty: 1, variant: null };

    function chosenOptions() {
      var out = [];
      questions.forEach(function (q) {
        var v = state.answers[q.id];
        if (v == null || q.type === 'input') return;
        var o = findOption(q, v);
        if (o) out.push(o);
      });
      return out;
    }

    function wireRetake(scope) {
      var b = scope.querySelector('[data-quiz-retake]');
      if (b && !b.__wired) { b.__wired = true; b.addEventListener('click', function () { start(true); }); }
    }

    function showOffer(announce) {
      var d = derive();
      var picks = picksFor(d);
      var top = catalog[picks[0]];
      el.stage.hidden = true;
      el.results.hidden = false;
      paint();
      if (!top) {
        root.classList.remove('is-offer');
        el.results.innerHTML = '<section class="qq"><h1>' + esc(offer.noMatch || 'Nothing matched yet') + '</h1></section><div class="qnav"><button type="button" class="qlink" data-quiz-retake>' + esc(offer.retake || 'Change my answers') + '</button></div>';
        wireRetake(el.results);
        return;
      }
      var qty = 1, note = '';
      chosenOptions().forEach(function (o) { if (+o.qty > 0) qty = +o.qty; if (o.note) note = o.note; });
      var tiers = (offer.tiers || []).filter(function (t) { return +t.qty > 0; });
      if (tiers.length && !tiers.some(function (t) { return +t.qty === qty; })) qty = +tiers[0].qty;
      var variants = (top.variants || []).filter(function (v) { return v.available !== false; });
      if (!variants.length) variants = top.variants || [];
      offerUI.qty = qty;
      offerUI.variant = variants[0] || { id: top.variantId, title: '', price: top.price };
      var gifts = offer.gifts || [];
      var html = '<div class="qo">' +
        '<div class="qo-top"><span class="pill">' + esc(offer.matchLabel || 'Your match') + '</span>' +
        (state.discount ? '<span class="pill g">' + esc(String(offer.codeLabel || 'Code [code] applied').replace('[code]', state.discount)) + '</span>' : '') +
        (note ? '<span class="qo-note">' + esc(note) + '</span>' : '') + '</div>' +
        '<div class="qo-l">' +
        '<div class="qo-prod">' + (top.image ? '<img src="' + esc(top.image) + '" alt="" loading="lazy">' : '<span class="qo-noimg"></span>') +
        '<div><h2>' + esc(top.short) + '</h2>' + (top.line ? '<p>' + esc(top.line) + '</p>' : '') + '</div></div>' +
        '<div class="qo-price"><b data-qo-price></b><s data-qo-was></s><span data-qo-note></span></div>' +
        '</div>' +
        '<div class="qo-r">' +
        (variants.length > 1 ? '<div class="field"><label>' + esc(offer.flavourLabel || 'Flavour') + '</label><div class="select" data-qo-select data-more-one="' + esc(offer.moreOne || '1 more flavour') + '" data-more-other="' + esc(offer.moreOther || '[n] more flavours') + '"><button type="button" class="cur" data-qo-cur><span></span></button><div class="menu">' +
          variants.map(function (v, i) { return '<button type="button" data-qo-variant="' + esc(v.id) + '"' + (i === 0 ? ' class="on"' : '') + '><span>' + esc(v.title) + '</span></button>'; }).join('') + '</div></div></div>' : '') +
        (tiers.length ? '<div class="tiers qo-tiers">' + tiers.map(function (t) {
          return '<button type="button" class="tier' + (+t.qty === qty ? ' on' : '') + '" data-qo-tier="' + (+t.qty) + '"><span class="r"></span><span class="t"><b>' + esc(t.label) + (t.tag ? '<span class="tg">' + esc(t.tag) + '</span>' : '') + '</b><small>' + esc(t.sub || '') + '</small></span><span class="p" data-qo-tier-price></span></button>';
        }).join('') + '</div>' : '') +
        (gifts.length ? '<div class="qo-gifts"><div class="gl">' + esc(offer.giftsLabel || 'Free with your order') + '</div>' + gifts.map(function (g) {
          return '<div class="gr" data-qo-gift="' + (+g.need || 1) + '" data-qo-spend="' + (+g.spend || 0) + '">' + (g.image ? '<img src="' + esc(g.image) + '" alt="" loading="lazy">' : '<span class="ic"></span>') + '<span class="gt"><b>' + esc(g.label) + '</b>' + (g.condition ? '<small>' + esc(g.condition) + '</small>' : '') + '</span><span class="st" data-qo-gift-state></span></div>';
        }).join('') + '</div>' : '') +
        '<button type="button" class="btn" data-qo-atc></button>' +
        (offer.fine ? '<p class="note">' + esc(offer.fine) + '</p>' : '') +
        '<button type="button" class="qlink" data-quiz-retake>' + esc(offer.retake || 'Change my answers') + '</button>' +
        '</div>' +
        '</div>';
      el.results.innerHTML = html;
      root.classList.add('is-offer');
      wireOffer(top);
      wireRetake(el.results);
      if (announce) track('Quiz Recommendations Shown', { top_product: top.handle, top_collection: '' });
      toTop();
    }

    function wireOffer(top) {
      var box = el.results;
      var sel = box.querySelector('[data-qo-select]');
      if (sel) {
        sel.querySelector('[data-qo-cur]').addEventListener('click', function () { sel.classList.toggle('open'); });
        document.addEventListener('click', function (e) { if (!sel.contains(e.target)) sel.classList.remove('open'); });
        Array.prototype.forEach.call(sel.querySelectorAll('[data-qo-variant]'), function (b) {
          b.addEventListener('click', function () {
            var id = b.getAttribute('data-qo-variant');
            var v = (top.variants || []).filter(function (x) { return String(x.id) === id; })[0];
            if (v) offerUI.variant = v;
            Array.prototype.forEach.call(sel.querySelectorAll('[data-qo-variant]'), function (x) { x.classList.toggle('on', x === b); });
            sel.classList.remove('open');
            paintOffer();
          });
        });
      }
      Array.prototype.forEach.call(box.querySelectorAll('[data-qo-tier]'), function (t) {
        t.addEventListener('click', function () {
          offerUI.qty = +t.getAttribute('data-qo-tier') || 1;
          Array.prototype.forEach.call(box.querySelectorAll('[data-qo-tier]'), function (x) { x.classList.toggle('on', x === t); });
          paintOffer();
        });
      });
      box.querySelector('[data-qo-atc]').addEventListener('click', function () { addOffer(top); });
      paintOffer();
    }

    function paintOffer() {
      var box = el.results, v = offerUI.variant, qty = offerUI.qty;
      var unit = priceOf({ price: v.price, compare: 0 });
      var cur = box.querySelector('[data-qo-cur] span');
      if (cur) cur.textContent = v.title || '';
      box.querySelector('[data-qo-price]').textContent = money(unit.now);
      box.querySelector('[data-qo-was]').textContent = unit.was ? money(unit.was) : '';
      box.querySelector('[data-qo-note]').textContent = (offer.perTub || 'per tub') + (state.discount ? ' ' + String(offer.withCode || 'with [code]').replace('[code]', state.discount) : '');
      Array.prototype.forEach.call(box.querySelectorAll('[data-qo-tier]'), function (t) {
        var q = +t.getAttribute('data-qo-tier'), p = t.querySelector('[data-qo-tier-price]');
        if (p) p.innerHTML = esc(money(unit.now * q)) + (unit.was ? '<s>' + esc(money(unit.was * q)) + '</s>' : '');
      });
      Array.prototype.forEach.call(box.querySelectorAll('[data-qo-gift]'), function (g) {
        // a spend gift (the towel at $130, 25 Oct 2026) is on once the tubs' total reaches it
        var need = +g.getAttribute('data-qo-gift') || 1, spend = +g.getAttribute('data-qo-spend') || 0, total = unit.now * qty;
        var on = qty >= need && (spend <= 0 || total >= spend), more = need - qty;
        g.classList.toggle('on', on);
        g.querySelector('[data-qo-gift-state]').textContent = on ? (offer.giftOn || 'Included') : (more > 0 ? (more === 1 ? (offer.giftOneMore || 'Add a tub') : String(offer.giftMore || 'Add [n] tubs').replace('[n]', more)) : 'Add ' + money(spend - total) + ' more');
      });
      box.querySelector('[data-qo-atc]').textContent = String(offer.atc || 'Add [n] to cart · [total]').replace('[n]', qty + (qty === 1 ? ' tub' : ' tubs')).replace('[total]', money(unit.now * qty));
    }

    // The buy box's rule, again: a gift is earned by tubs of this product,
    // spend across the paid lines, one-time or subscription; stamped so the
    // cart drawer sweeps it the moment the rule stops holding.
    function earnedGifts(top, cart) {
      var isGift = function (i) { return !!(i.properties && (i.properties._lp_gift || i.properties._gwp || i.properties._gift)); };
      var items = cart.items || [];
      var paid = items.filter(function (i) { return !isGift(i); });
      var present = {};
      items.forEach(function (i) { if (isGift(i)) present[String(i.variant_id)] = 1; });
      var spend = paid.reduce(function (t, i) { return t + (i.final_line_price || 0); }, 0);
      var tubs = paid.filter(function (i) { return String(i.product_id) === String(top.id); }).reduce(function (t, i) { return t + i.quantity; }, 0);
      return (offer.gifts || []).filter(function (g) {
        if (!g.variant || present[String(g.variant)] || !paid.length) return false;
        if (+g.need > 1 && tubs < +g.need) return false;
        if (+g.spend > 0 && spend < +g.spend) return false;
        if (g.mode === 'subscription' && !paid.some(function (i) { return i.selling_plan_allocation; })) return false;
        if (g.mode === 'onetime' && paid.every(function (i) { return i.selling_plan_allocation; })) return false;
        return true;
      }).map(function (g) {
        return { id: +g.variant, quantity: 1, properties: { _lp_gift: g.label, _lp_gift_min: String(g.need || 1), _lp_gift_mode: g.mode || 'both', _lp_gift_spend: String(g.spend || 0), _lp_gift_pid: String(top.id) } };
      });
    }

    function addOffer(top) {
      var btn = el.results.querySelector('[data-qo-atc]'), v = offerUI.variant, qty = offerUI.qty;
      if (!v || !v.id) return;
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = offer.adding || 'Adding…';
      track('Add To Cart', { variant: String(v.id), quantity: qty, discount: state.discount || '' });
      var paid = { id: +v.id, quantity: qty };
      var post = function (items) {
        return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items }) })
          .then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); });
      };
      post([paid])
        .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
        .then(function (cart) { var g = earnedGifts(top, cart); return g.length ? post(g) : null; })
        .then(function () { btn.textContent = offer.added || 'Added'; openCart([paid]); })
        .catch(function () { btn.textContent = offer.failed || 'Could not add, try the product page'; })
        .then(function () { setTimeout(function () { btn.disabled = false; btn.textContent = label; }, 2200); });
    }

    // ------------------------------------------------------------ cart
    function openCart(items) {
      document.dispatchEvent(new CustomEvent('hx:atc', { detail: { items: items } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      if (window.HPS && window.HPS.cart && window.HPS.cart.open) return window.HPS.cart.open();
      var t = document.querySelector('[data-cart-toggle],.cart-drawer-toggle');
      if (t) t.click();
    }

    function wireAtc(scope) {
      Array.prototype.forEach.call(scope.querySelectorAll('[data-atc]'), function (btn) {
        if (btn.__wired) return;
        btn.__wired = true;
        btn.addEventListener('click', function () {
          var ids = String(btn.getAttribute('data-atc') || '').split(',').filter(Boolean);
          if (!ids.length) return;
          var label = btn.textContent;
          btn.disabled = true;
          btn.textContent = 'Adding…';
          track('Add To Cart', { variant: ids.join(','), discount: state.discount || '' });
          var items = ids.map(function (id) { return { id: +id, quantity: 1 }; });
          fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items }) })
            .then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); btn.textContent = 'Added'; openCart(items); })
            .catch(function () { btn.textContent = 'Could not add, try the product page'; })
            .then(function () { setTimeout(function () { btn.disabled = false; btn.textContent = label; }, 2200); });
        });
      });
    }

    // ------------------------------------------------------------ start screen
    // The section renders it in a <template> so the icons and the final-word
    // accent come from Liquid; this copies it in, hides the step count and the
    // bar while it shows, and moves on to question 1 when the button is pressed.
    function startTemplate() { return root.querySelector('[data-quiz-start]'); }
    function showStart() {
      el.stage.classList.add('is-start');
      showStage(startTemplate().innerHTML + ((flow.reviewScreens === 'both' || flow.reviewScreens === 'start') ? reviewHtml(0) : ''));
      var go = el.screen.querySelector('[data-quiz-start-go]');
      if (go) go.addEventListener('click', function () {
        state.startDone = true; save();
        track('Quiz Start Clicked');
        showQuestion();
      });
    }

    // ------------------------------------------------------------ start
    function start(fromScratch) {
      if (fromScratch) { clear(); state = fresh(); }
      root.classList.remove('is-offer');
      if (!offer) track('Quiz Started', { total_questions: total });
      if (flow.start && startTemplate() && !state.startDone) showStart(); else showQuestion();
    }

    if (el.back) el.back.addEventListener('click', goBack);
    if (el.exit) el.exit.addEventListener('click', clear);
    if (el.retake) el.retake.addEventListener('click', function () { start(true); });

    if (state.done) showResults(false);
    else if (state.step > 0 || Object.keys(state.answers).length) {
      if (state.step >= total) state.step = total - 1;
      showQuestion();
    } else start(false);
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-hx-quiz]'), init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector && e.target.querySelector('[data-hx-quiz]');
    if (root) { root.__hxQuiz = false; init(root); }
  });
})();
